import { useEffect, useState } from "react";
import { Mic, Video, FileText, TrendingUp } from "lucide-react";
import { Field } from "./atoms";
import { api } from "../../lib/api";

// Money formatter for the LOI nudge — mirrors StepReview's dual-glyph
// style ("CAD $4.66") so the client sees a consistent currency call-out
// across the wizard.
function fmtDelta(amount, currency) {
  const n = Number(amount || 0);
  const abs = Math.abs(n).toFixed(2);
  if (currency && currency !== "USD") return `${currency} $${abs}`;
  return `$${abs}`;
}

/**
 * Step 0 — "Brief": title, target, language, MODALITY cards, recurrence,
 * compliance.
 *
 * Note (2026-02 UI polish): the live Invoice Summary card that formerly
 * lived at the bottom of this step has been removed per the operational
 * spec. Pricing / FX localization now surfaces on Step Review and the
 * post-submit checkout screens only.
 */
const MODALITY_CARDS = [
  {
    id: "text",
    label: "Text-Only",
    icon: FileText,
    blurb: "Written open-ended responses. No hardware required.",
    markup: "CAD $1.50 / complete",
  },
  {
    id: "audio",
    label: "Audio-First",
    icon: Mic,
    blurb: "Voice qualitative phase with Whisper transcription, GPT-5.2 probes, and librosa prosody analysis.",
    markup: "CAD $3.00 / complete",
  },
  {
    id: "video",
    label: "Video-First",
    icon: Video,
    blurb: "Full multi-modal capture (front-facing 1280×720 webcam + voice). Desktop required.",
    markup: "CAD $5.00 / complete",
  },
];

export default function StepBrief({ form, setForm }) {
  // Live LOI cost-delta nudge — fetches PS-priced CPI at current LOI AND
  // at a 5-min baseline in one round-trip. Debounced 400ms so slider drags
  // don't hammer the backend. Only fires when LOI > baseline so the nudge
  // is upsell-neutral for shorter studies.
  const [loiDelta, setLoiDelta] = useState(null);
  const [modalityYield, setModalityYield] = useState(null);
  const BASELINE_LOI = 5;
  useEffect(() => {
    const cur = parseInt(form.expected_loi) || 0;
    if (cur <= BASELINE_LOI) {
      setLoiDelta(null);
      return;
    }
    const ctl = new AbortController();
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/campaigns/loi-cost-delta", {
          modality: form.modality,
          target_completes: form.target_completes || 100,
          qualitative_questions: form.qualitative_questions || [],
          target_demographics: form.target_demographics || {},
          expected_ir: form.expected_ir,
          expected_loi: cur,
          field_time: form.field_time,
          baseline_loi: BASELINE_LOI,
        }, { signal: ctl.signal });
        if (!cancelled) setLoiDelta(data);
      } catch (e) {
        if (!cancelled && e.name !== "CanceledError" && e.name !== "AbortError") setLoiDelta(null);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); ctl.abort(); };
  }, [
    form.expected_loi, form.expected_ir, form.field_time, form.modality,
    form.target_completes, form.target_demographics,
    form.qualitative_questions,
  ]);

  // Modality → estimated panel-reach hint. Advisory only — clients still
  // pick freely; the number just makes the tradeoff visible before they
  // lock modality. Debounced 400ms; hidden when the PS provider isn't
  // configured (dev sandbox) OR the audience is too broad for a stable
  // baseline count.
  useEffect(() => {
    const ctl = new AbortController();
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/campaigns/modality-yield", {
          target_completes: form.target_completes || 100,
          target_demographics: form.target_demographics || {},
          expected_ir: form.expected_ir || 50,
          expected_loi: form.expected_loi || 5,
          field_time: form.field_time || 7,
        }, { signal: ctl.signal });
        if (!cancelled) setModalityYield(data);
      } catch (e) {
        if (!cancelled && e.name !== "CanceledError" && e.name !== "AbortError") {
          setModalityYield(null);
        }
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); ctl.abort(); };
  }, [
    form.target_completes, form.target_demographics,
    form.expected_ir, form.expected_loi, form.field_time,
  ]);
  return (
    <div className="space-y-5" data-testid="step-brief">
      <Field label="Campaign title">
        <input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
          data-testid="input-title"
          placeholder="e.g. New iced coffee launch — Halifax"
        />
      </Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Target completes">
          <input
            type="number"
            min={10}
            value={form.target_completes}
            onChange={(e) => setForm({ ...form, target_completes: parseInt(e.target.value) || 0 })}
            className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
            data-testid="input-target-completes"
          />
        </Field>
        <Field label="Interview language">
          <div className="inline-flex border border-[var(--rl-border)] bg-white" role="radiogroup" data-testid="lang-toggle">
            {[
              { v: "en", label: "English" },
              { v: "fr", label: "Français" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                role="radio"
                aria-checked={form.language === opt.v}
                onClick={() => setForm({ ...form, language: opt.v })}
                className={`px-4 py-2 text-sm font-semibold transition-colors ${form.language === opt.v ? "bg-[var(--rl-text)] text-white" : "text-[var(--rl-text)] hover:bg-[var(--rl-bg-subtle)]"}`}
                data-testid={`lang-toggle-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Field>
      </div>

      {/* MODALITY SELECTION — drives hardware preflight + pricing tier */}
      <Field label="Collection modality">
        <div className="grid sm:grid-cols-3 gap-3" role="radiogroup" data-testid="modality-radiogroup">
          {MODALITY_CARDS.map((m) => {
            const Icon = m.icon;
            const active = form.modality === m.id;
            const reachPct = modalityYield
              ? (m.id === "video"
                  ? modalityYield.video_yield_pct
                  : (m.id === "audio"
                      ? modalityYield.audio_yield_pct
                      : modalityYield.text_yield_pct))
              : null;
            const showReach = modalityYield?.provider_available && reachPct !== null && reachPct !== undefined;
            // Amber below 30% panel reach — nudges the client to
            // reconsider video for tight audiences without blocking.
            const reachClass = showReach && m.id === "video" && reachPct < 30
              ? "text-amber-600"
              : "text-[var(--rl-text-secondary)]";
            return (
              <button
                type="button"
                role="radio"
                aria-checked={active}
                key={m.id}
                onClick={() => setForm({ ...form, modality: m.id, video_enabled: m.id === "video" })}
                className={`text-left p-4 border-2 transition-colors ${active ? "border-[var(--rl-brand)] bg-[var(--rl-brand)]/5" : "border-[var(--rl-border)] bg-white hover:border-[var(--rl-text-secondary)]"}`}
                data-testid={`modality-card-${m.id}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`w-5 h-5 ${active ? "text-[var(--rl-brand)]" : "text-[var(--rl-text-secondary)]"}`} />
                  <div className="font-bold text-sm">{m.label}</div>
                </div>
                <div className="text-[11px] text-[var(--rl-text-secondary)] mt-2 leading-relaxed">{m.blurb}</div>
                {showReach && (
                  <div
                    className={`text-[10px] uppercase tracking-[0.2em] font-bold mt-3 ${reachClass}`}
                    data-testid={`modality-reach-${m.id}`}
                  >
                    ~{reachPct}% panel reach
                    {m.id === "video" && reachPct < 30 && (
                      <span className="ml-1 normal-case tracking-normal font-medium">
                        · consider audio
                      </span>
                    )}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold mt-3 text-[var(--rl-text)]">Platform fees {m.markup}</div>
              </button>
            );
          })}
        </div>
      </Field>

      {/* PS-MANDATORY STUDY PARAMETERS — required by PureSpectrum on every
          /feasibility + /surveys request. We surface them prominently on
          Step Brief so the client sets them consciously; the wizard blocks
          progress until all three are within valid ranges. */}
      <Field
        label="Study parameters"
        hint="Required fields to efficiently price and route your survey"
      >
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)] mb-1.5">
              Incidence rate (%)
            </span>
            <input
              type="number"
              min={1}
              max={100}
              value={form.expected_ir}
              onChange={(e) => setForm({ ...form, expected_ir: Math.max(1, Math.min(100, parseInt(e.target.value) || 0)) })}
              className="w-full bg-white border border-[var(--rl-border)] px-3 py-2.5 focus:outline-none focus:border-[var(--rl-brand)] font-mono text-sm"
              data-testid="input-expected-ir"
            />
            <span className="block text-[10px] font-mono text-[var(--rl-text-secondary)] mt-1">
              % of panel that will qualify
            </span>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)] mb-1.5">
              Interview length (min)
            </span>
            <input
              type="number"
              min={1}
              max={60}
              value={form.expected_loi}
              onChange={(e) => setForm({ ...form, expected_loi: Math.max(1, Math.min(60, parseInt(e.target.value) || 0)) })}
              className="w-full bg-white border border-[var(--rl-border)] px-3 py-2.5 focus:outline-none focus:border-[var(--rl-brand)] font-mono text-sm"
              data-testid="input-expected-loi"
            />
            <span className="block text-[10px] font-mono text-[var(--rl-text-secondary)] mt-1">
              Median session in minutes
            </span>
            {/* Live cost-delta nudge — only shown when LOI > baseline so we
                don't harangue clients running a snappy 3-min screener. */}
            {loiDelta && loiDelta.delta_cpc > 0 && (
              <div
                className="mt-2 inline-flex items-start gap-1.5 px-2 py-1.5 bg-amber-50 border-l-2 border-amber-500 text-[10px] leading-tight text-amber-900"
                data-testid="loi-cost-nudge"
                data-delta={loiDelta.delta_cpc}
              >
                <TrendingUp className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-600" />
                <span>
                  A <b>{loiDelta.current_loi}-min</b> survey costs{" "}
                  <b className="whitespace-nowrap">
                    ~{fmtDelta(loiDelta.delta_cpc, loiDelta.currency)}
                  </b>{" "}
                  more per complete than {loiDelta.baseline_loi}-min.
                </span>
              </div>
            )}
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)] mb-1.5">
              Fielding window (days)
            </span>
            <input
              type="number"
              min={1}
              max={90}
              value={form.field_time}
              onChange={(e) => setForm({ ...form, field_time: Math.max(1, Math.min(90, parseInt(e.target.value) || 0)) })}
              className="w-full bg-white border border-[var(--rl-border)] px-3 py-2.5 focus:outline-none focus:border-[var(--rl-brand)] font-mono text-sm"
              data-testid="input-field-time"
            />
            <span className="block text-[10px] font-mono text-[var(--rl-text-secondary)] mt-1">
              Data collection duration
            </span>
          </label>
        </div>
      </Field>

      {/* Comparative + recurrence + compliance */}
      <Field label="Comparative study group (optional)">
        <input
          value={form.compare_group_id || ""}
          onChange={(e) => setForm({ ...form, compare_group_id: e.target.value || null })}
          className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
          placeholder="iced-coffee-2026"
          data-testid="input-compare-group"
        />
      </Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Recurrence">
          <select
            value={form.recurrence_cadence || "none"}
            onChange={(e) => setForm({ ...form, recurrence_cadence: e.target.value })}
            className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
            data-testid="input-recurrence-cadence"
          >
            <option value="none">One-time study</option>
            <option value="monthly">Monthly wave</option>
            <option value="quarterly">Quarterly wave</option>
            <option value="yearly">Yearly wave</option>
          </select>
        </Field>
        <Field label="Compliance mode">
          <select
            value={form.compliance_mode || "standard"}
            onChange={(e) => setForm({ ...form, compliance_mode: e.target.value })}
            className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
            data-testid="input-compliance-mode"
          >
            <option value="standard">Standard (PIPEDA)</option>
            <option value="gdpr">GDPR — EU</option>
            <option value="ccpa">CCPA — California</option>
            <option value="hipaa">HIPAA-aligned</option>
          </select>
        </Field>
      </div>
    </div>
  );
}
